import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';
import { trackEmailClick, trackFormSubmission, trackPhoneCall } from '../../components/Analytics/Analytics';
import '../AplicaIA2026Page/AplicaIA2026Page.css';

const WEBHOOK_URL =
  process.env.REACT_APP_BONO_IA_WEBHOOK_URL ||
  process.env.REACT_APP_APLICA_IA_WEBHOOK_URL ||
  'https://hook.eu1.make.com/ag4wg4gyru5tebyiuyhnre6iz25ekidj';

const CONTACT_EMAIL = process.env.REACT_APP_BONO_IA_EMAIL_TO || 'comercial@e-asy.es';
const CONTACT_PHONE = '+34 822 68 50 12';
const PAGE_PATH = '/servicios/bono-ia-plan-ia360';
const PAGE_URL = `https://e-asy.es${PAGE_PATH}`;

const medidas = [
  {
    titulo: 'Bono empresarial de IA',
    descripcion:
      '600 millones de euros previstos para llegar a unas 25.000 empresas. La orientación anunciada es financiar desarrollo, integración, datos y rediseño de procesos, no una suscripción de software.',
  },
  {
    titulo: 'Diagnóstico y caso de uso',
    descripcion:
      'El planteamiento público parte de evaluar la madurez y definir un uso concreto de IA sobre un proceso real, con indicadores para medir después su impacto.',
  },
  {
    titulo: 'Proveedor con valor añadido',
    descripcion:
      'Los servicios anunciados corresponden a empresas tecnológicas europeas capaces de desarrollar, integrar y adaptar la solución al negocio.',
  },
  {
    titulo: 'Calendario todavía abierto',
    descripcion:
      'Se ha anunciado un piloto durante el primer semestre de 2027 y una convocatoria general antes de finalizar ese año. Hoy no hay bases ni solicitudes.',
  },
];

const preparacion = [
  {
    numero: '01',
    titulo: 'Diagnóstico de madurez',
    descripcion:
      'Revisamos procesos, sistemas, calidad de los datos y capacidad del equipo para distinguir una oportunidad real de una prueba aislada.',
  },
  {
    numero: '02',
    titulo: 'Caso de uso prioritario',
    descripcion:
      'Elegimos un proceso con volumen, coste o riesgo suficientes y definimos qué decisión o tarea debe mejorar la IA.',
  },
  {
    numero: '03',
    titulo: 'Datos y sistemas',
    descripcion:
      'Identificamos las fuentes, permisos, integraciones y límites de privacidad necesarios antes de construir nada.',
  },
  {
    numero: '04',
    titulo: 'Solución y alcance',
    descripcion:
      'Convertimos el caso en un alcance técnico: asistente, automatización, agente, integración con ERP o tratamiento documental.',
  },
  {
    numero: '05',
    titulo: 'Indicadores de impacto',
    descripcion:
      'Fijamos antes del piloto tiempos, errores, coste por operación o calidad del servicio para poder demostrar el resultado.',
  },
  {
    numero: '06',
    titulo: 'Cumplimiento desde el diseño',
    descripcion:
      'Revisamos privacidad, seguridad, trazabilidad y encaje con el Reglamento Europeo de IA según el uso previsto.',
  },
];

const proyectos = [
  {
    titulo: 'Asistente sobre documentación interna',
    descripcion:
      'Respuestas fundamentadas en procedimientos, contratos o expedientes, con control de accesos y registro de las fuentes utilizadas.',
  },
  {
    titulo: 'Automatización documental',
    descripcion:
      'Lectura y clasificación de facturas, formularios o solicitudes para reducir la carga manual y los errores de transcripción.',
  },
  {
    titulo: 'Agente conectado al ERP',
    descripcion:
      'Consultas y tareas operativas sobre pedidos, inventario, clientes o expedientes sin duplicar la información en otra herramienta.',
  },
  {
    titulo: 'Apoyo a atención y presupuestos',
    descripcion:
      'Un primer filtro que entiende la necesidad, reúne los datos y prepara una respuesta o estimación para que la revise el equipo.',
  },
];

const checklist = [
  'El proceso candidato tiene un responsable y un volumen medible.',
  'Existen datos suficientes y se conoce dónde residen.',
  'Está definido qué puede hacer la IA y qué debe validar una persona.',
  'Los sistemas implicados pueden integrarse mediante API o una exportación controlada.',
  'Hay indicadores de partida: tiempo, errores, coste o calidad.',
  'Se han identificado datos personales, sensibles o sujetos a secreto profesional.',
];

const faqs = [
  {
    question: '¿Ya se puede solicitar el Bono IA?',
    answer:
      'No. A 13 de octubre de 2026 está anunciado, pero no hay bases, organismo gestor, importe por empresa ni plazo de solicitud. El calendario público sitúa el piloto en el primer semestre de 2027 y la convocatoria general antes de que termine 2027.',
  },
  {
    question: '¿Los 600 millones son la ayuda de cada empresa?',
    answer:
      'No. Son la dotación global anunciada para el instrumento. El objetivo citado es alcanzar unas 25.000 empresas, pero dividir ambas cifras no constituye un importe oficial ni una ayuda garantizada.',
  },
  {
    question: '¿Qué tipo de proyecto encaja mejor con lo anunciado?',
    answer:
      'El plan orienta la financiación hacia desarrollo, integración, tratamiento de datos y rediseño de procesos realizado por una empresa tecnológica europea. Excluye expresamente la mera contratación de licencias.',
  },
  {
    question: '¿Para qué sirve prepararse antes de la convocatoria?',
    answer:
      'Permite llegar con un caso de uso, un diagnóstico, datos localizados y métricas definidas. Ese trabajo tiene utilidad propia aunque la empresa finalmente no solicite la ayuda o no resulte beneficiaria.',
  },
  {
    question: '¿E-ASY garantiza la obtención del bono?',
    answer:
      'No. Nadie puede garantizar una ayuda cuyas bases aún no existen. Preparamos el proyecto y, cuando se publique la convocatoria, revisaremos su encaje y los requisitos aplicables.',
  },
];

const initialFormData = {
  nombre: '',
  empresa: '',
  email: '',
  telefono: '',
  territorio: '',
  proceso: '',
  mensaje: '',
  consentimiento: false,
};

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const BonoIAPage = ({ darkMode, toggleDarkMode }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const buildEmailHtml = () => [
    '<h3>Nuevo lead desde la landing Bono IA · Plan IA360</h3>',
    '<p>Se ha recibido una solicitud de diagnóstico desde la web de E-ASY.</p>',
    `<p><strong>Nombre:</strong> ${escapeHtml(formData.nombre)}</p>`,
    `<p><strong>Empresa:</strong> ${escapeHtml(formData.empresa)}</p>`,
    `<p><strong>Email:</strong> ${escapeHtml(formData.email)}</p>`,
    `<p><strong>Teléfono:</strong> ${escapeHtml(formData.telefono || 'No indicado')}</p>`,
    `<p><strong>Territorio:</strong> ${escapeHtml(formData.territorio || 'No indicado')}</p>`,
    `<p><strong>Proceso candidato:</strong> ${escapeHtml(formData.proceso || 'No indicado')}</p>`,
    `<p><strong>Mensaje:</strong><br>${escapeHtml(formData.mensaje || 'Sin mensaje adicional').replace(/\n/g, '<br>')}</p>`,
    `<p><strong>URL de origen:</strong> ${escapeHtml(window.location.href)}</p>`,
  ].join('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: CONTACT_EMAIL,
          cc: '',
          cc1: '',
          cc2: '',
          cc3: '',
          subject: `[Bono IA 360] ${formData.empresa || formData.nombre}`,
          text: buildEmailHtml(),
        }),
      });

      if (!response.ok) {
        throw new Error('No se pudo enviar el formulario');
      }

      trackFormSubmission('bono_ia_plan_ia360_form');
      setEnviado(true);
      setFormData(initialFormData);
    } catch (submitError) {
      setError(`No se pudo enviar la solicitud. Escríbenos a ${CONTACT_EMAIL}.`);
    } finally {
      setEnviando(false);
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    url: PAGE_URL,
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Preparación de proyectos para el Bono IA del Plan IA360',
    serviceType: 'Consultoría de inteligencia artificial',
    url: PAGE_URL,
    areaServed: 'ES',
    provider: {
      '@type': 'Organization',
      name: 'E-ASY',
      url: 'https://e-asy.es',
    },
    description:
      'Diagnóstico y definición de proyectos de inteligencia artificial para que una empresa llegue preparada a la futura convocatoria del Bono IA.',
  };

  return (
    <>
      <SEO
        title="Bono IA y Plan IA360: prepara tu proyecto | E-ASY"
        description="Diagnóstico para preparar un proyecto real de IA antes del Bono IA del Plan IA360. Sin convocatoria abierta: revisamos procesos, datos, alcance e indicadores."
        keywords="Bono IA, Plan IA360, ayuda inteligencia artificial pymes, diagnóstico IA, proyecto IA subvencionable, E-ASY Canarias"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-bono-ia-faq" data={faqSchema} />
      <JsonLd id="jsonld-bono-ia-service" data={serviceSchema} />
      <AnalyticsTracker pageTitle="Bono IA · Plan IA360 - E-ASY" pageLocation={PAGE_PATH} darkMode={darkMode} />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className={`aplica-ia-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="aplica-ia-hero">
          <div className="aplica-ia-hero-content">
            <div className="aplica-ia-hero-text">
              <span className="aplica-ia-eyebrow">Plan IA360 · Preparación de proyectos</span>
              <h1 className="aplica-ia-title">Llega a la convocatoria del Bono IA con el proyecto ya definido</h1>
              <p className="aplica-ia-subtitle">
                El Plan IA360 anuncia 600 millones de euros para que unas 25.000 empresas integren inteligencia
                artificial en procesos reales. La convocatoria todavía no está abierta y no prometemos financiación:
                preparamos el diagnóstico, el caso de uso y la base técnica para llegar a tiempo.
              </p>
              <div className="aplica-ia-hero-actions">
                <a href="#diagnostico-bono-ia" className="aplica-ia-button primary">Solicitar diagnóstico</a>
                <Link to="/soluciones-ia" className="aplica-ia-button secondary">Ver soluciones de IA</Link>
              </div>
              <div className="aplica-ia-stats">
                <div className="aplica-ia-stat"><strong>600 M€</strong><span>dotación global anunciada</span></div>
                <div className="aplica-ia-stat"><strong>25.000</strong><span>empresas objetivo del plan</span></div>
                <div className="aplica-ia-stat"><strong>2027</strong><span>piloto y convocatoria prevista</span></div>
              </div>
            </div>
            <div className="aplica-ia-hero-card">
              <div className="hero-card-panel">
                <h2>Qué puedes dejar resuelto ahora</h2>
                <ul>
                  <li>Un proceso concreto donde la IA pueda generar retorno.</li>
                  <li>Los datos, sistemas y permisos que necesita el proyecto.</li>
                  <li>Un alcance técnico que no dependa de comprar licencias.</li>
                  <li>Indicadores para medir el resultado del piloto.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="aplica-ia-section">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Qué se sabe y qué sigue pendiente</h2>
              <p>
                Tomamos como referencia la información pública del Plan IA360. Las condiciones finales solo podrán
                confirmarse cuando se publiquen las bases.
              </p>
            </div>
            <div className="aplica-ia-grid cards-2">
              {medidas.map((medida) => (
                <article key={medida.titulo} className="aplica-ia-card feature">
                  <h3>{medida.titulo}</h3>
                  <p>{medida.descripcion}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="aplica-ia-section aplica-ia-alt">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Cómo preparamos el proyecto</h2>
              <p>El resultado es útil para decidir una inversión hoy y para documentar una solicitud cuando exista.</p>
            </div>
            <div className="aplica-ia-grid cards-3">
              {preparacion.map((paso) => (
                <article key={paso.numero} className="aplica-ia-card">
                  <div className="aplica-ia-card-icon">{paso.numero}</div>
                  <h3>{paso.titulo}</h3>
                  <p>{paso.descripcion}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="aplica-ia-section">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Proyectos que podemos definir</h2>
              <p>Partimos del proceso de la empresa y lo conectamos con los sistemas que ya utiliza.</p>
            </div>
            <div className="aplica-ia-grid cards-2">
              {proyectos.map((proyecto) => (
                <article key={proyecto.titulo} className="aplica-ia-card">
                  <h3>{proyecto.titulo}</h3>
                  <p>{proyecto.descripcion}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="aplica-ia-section aplica-ia-alt">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-highlight-grid">
              <div className="aplica-ia-highlight">
                <h3>Checklist de preparación</h3>
                <ul>
                  {checklist.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
              <div className="aplica-ia-highlight emphasis">
                <h3>Qué aporta E-ASY</h3>
                <ul>
                  <li>Equipo propio en Canarias y proyectos desplegados en producción.</li>
                  <li>Integración con ERP, documentación, automatización y datos.</li>
                  <li>Experiencia en privacidad, seguridad y usos con datos sensibles.</li>
                  <li>Revisión del encaje cuando se publiquen las bases oficiales.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="aplica-ia-section">
          <div className="aplica-ia-section-content">
            <div className="aplica-ia-section-header">
              <h2>Preguntas frecuentes</h2>
            </div>
            <div className="aplica-ia-faqs">
              {faqs.map((item) => (
                <article key={item.question} className="faq-item">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="diagnostico-bono-ia" className="aplica-ia-section aplica-ia-form-section">
          <div className="aplica-ia-section-content form-layout">
            <div className="aplica-ia-form-copy">
              <span className="aplica-ia-eyebrow">Diagnóstico inicial</span>
              <h2>Cuéntanos qué proceso quieres mejorar</h2>
              <p>
                Revisaremos si existe un caso de uso real y qué información conviene reunir antes de la convocatoria.
                La solicitud no implica ningún compromiso de financiación.
              </p>
              <div className="contact-mini-cards">
                <div className="mini-card">
                  <span>Email</span>
                  <a href={`mailto:${CONTACT_EMAIL}`} onClick={() => trackEmailClick(CONTACT_EMAIL)}>{CONTACT_EMAIL}</a>
                </div>
                <div className="mini-card">
                  <span>Teléfono</span>
                  <a href={`tel:${CONTACT_PHONE.replace(/\s+/g, '')}`} onClick={() => trackPhoneCall(CONTACT_PHONE)}>{CONTACT_PHONE}</a>
                </div>
              </div>
            </div>

            <div className="aplica-ia-form-card">
              {enviado ? (
                <div className="aplica-ia-success">
                  <h3>Solicitud enviada</h3>
                  <p>Hemos recibido tus datos. Revisaremos el caso y te contactaremos para concretar el diagnóstico.</p>
                  <a href={PAGE_PATH} className="aplica-ia-button primary">Enviar otra solicitud</a>
                </div>
              ) : (
                <form className="aplica-ia-form" onSubmit={handleSubmit}>
                  <div className="form-row two-columns">
                    <label className="form-field"><span>Nombre y apellidos *</span><input name="nombre" value={formData.nombre} onChange={handleChange} required placeholder="Tu nombre" /></label>
                    <label className="form-field"><span>Empresa *</span><input name="empresa" value={formData.empresa} onChange={handleChange} required placeholder="Nombre de la empresa" /></label>
                  </div>
                  <div className="form-row two-columns">
                    <label className="form-field"><span>Email *</span><input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="tu@email.com" /></label>
                    <label className="form-field"><span>Teléfono</span><input type="tel" name="telefono" value={formData.telefono} onChange={handleChange} placeholder="Opcional" /></label>
                  </div>
                  <div className="form-row two-columns">
                    <label className="form-field"><span>Territorio</span><input name="territorio" value={formData.territorio} onChange={handleChange} placeholder="Ej. Tenerife, Canarias..." /></label>
                    <label className="form-field"><span>Proceso candidato</span><input name="proceso" value={formData.proceso} onChange={handleChange} placeholder="Ej. facturas, atención, expedientes..." /></label>
                  </div>
                  <label className="form-field">
                    <span>Cuéntanos el punto de partida</span>
                    <textarea name="mensaje" value={formData.mensaje} onChange={handleChange} rows="5" placeholder="Qué proceso quieres mejorar, qué sistemas usas y qué resultado esperas..." />
                  </label>
                  <label className="form-consent">
                    <input type="checkbox" name="consentimiento" checked={formData.consentimiento} onChange={handleChange} required />
                    <span>He leído la <Link to="/politica-privacidad">política de privacidad</Link> y acepto el tratamiento de mis datos para recibir respuesta a esta solicitud.</span>
                  </label>
                  {error && <p className="form-error">{error}</p>}
                  <button type="submit" className="aplica-ia-button primary form-submit" disabled={enviando}>
                    {enviando ? 'Enviando...' : 'Solicitar diagnóstico'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>

      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default BonoIAPage;
